import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Eyebrow, MaskLines, Reveal } from "../motion/Reveal";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { useSite } from "../../lib/site";

const EMPTY = {
  first_name: "", last_name: "", country: "", state: "", city: "", whatsapp: "", email: "",
  sales_experience: "", mlm_experience: "", business_contacts: "", referral_code: "", accepted_terms: false,
};
const TEXT_FIELDS = [
  ["first_name", "text", "given-name"],
  ["last_name", "text", "family-name"],
  ["state", "text", "address-level1"],
  ["city", "text", "address-level2"],
  ["whatsapp", "tel", "tel"],
  ["email", "email", "email"],
];
const YES_NO = ["sales_experience", "mlm_experience", "business_contacts"];
const fieldCls = "h-12 rounded-xl border-ink/15 bg-paper/60 text-base text-ink placeholder:text-ink/45 focus-visible:ring-leaf";

const Label = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="mb-2 block text-sm font-semibold text-ink/85">
    {children}
  </label>
);

const YesNo = ({ name, label, value, onChange }) => (
  <div className="sm:col-span-2">
    <p className="mb-2 text-sm font-semibold text-ink/85">{label}</p>
    <div className="flex gap-2" role="radiogroup" aria-label={label}>
      {[["si", "Sí"], ["no", "No"]].map(([v, l]) => (
        <button
          type="button"
          key={v}
          role="radio"
          aria-checked={value === v}
          onClick={() => onChange(name, v)}
          data-testid={`lead-form-${name}-${v}`}
          className={`flex h-11 min-w-[88px] items-center justify-center gap-2 rounded-full border px-5 text-sm font-semibold transition-[background-color,border-color,color] duration-300 ${
            value === v ? "border-ink bg-ink text-white" : "border-ink/20 bg-white text-ink hover:border-ink/50"
          }`}
        >
          {value === v && <Check className="h-4 w-4 text-leaf" />} {l}
        </button>
      ))}
    </div>
  </div>
);

const Success = ({ r, onReset }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="flex min-h-[520px] flex-col items-start justify-center" data-testid="lead-form-success">
    <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.1 }} className="grid h-20 w-20 place-items-center rounded-full bg-leaf">
      <CheckCircle2 className="h-10 w-10 text-ink" />
    </motion.span>
    <h3 className="mt-8 font-display text-4xl font-semibold tracking-tight">{r.success_title}</h3>
    <p className="mt-4 max-w-md text-lg text-ink/75">{r.success_text}</p>
    <button onClick={onReset} className="mt-8 font-mono text-xs uppercase tracking-[0.12em] text-leaf-deep underline-offset-4 hover:underline" data-testid="lead-form-reset">
      Registrar otra persona
    </button>
  </motion.div>
);

export const RegisterForm = () => {
  const { content } = useSite();
  const r = content.registro;
  const countries = content.paises.list || [];
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref.slice(0, 60) }));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const missing = YES_NO.find((k) => !form[k]) || (!form.country && "country");
    if (missing) return toast.error(`Completa: ${r.labels[missing]}`);
    if (!form.accepted_terms) return toast.error("Debes aceptar la declaración para continuar.");
    if (!API_BASE) return toast.error("El registro no está disponible en este momento.");
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="registro" className="relative bg-paper py-24 sm:py-32 lg:py-40" data-testid="registro-section">
      <div className="container-x grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Eyebrow tone="light">{r.eyebrow}</Eyebrow>
            <MaskLines lines={r.title_lines} className="h-section mt-6" testId="registro-title" />
            <Reveal className="mt-8 max-w-md text-lg leading-relaxed text-ink/80">
              <p>{r.text}</p>
            </Reveal>
            <ul className="mt-10 space-y-3">
              {r.assurances.map((a, i) => (
                <Reveal as="li" key={i} delay={i * 0.08} className="flex items-center gap-3 text-base font-semibold">
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-leaf"><Check className="h-4 w-4 text-ink" strokeWidth={3} /></span>
                  {a}
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
        <Reveal className="rounded-[2rem] border border-ink/10 bg-white p-6 shadow-[0_40px_80px_-50px_rgba(10,18,48,.35)] sm:p-10 lg:col-span-7">
          <AnimatePresence initial={false}>
            {done ? (
              <Success key="ok" r={r} onReset={() => setDone(false)} />
            ) : (
              <motion.form key="form" onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="lead-form" noValidate={false}>
                {TEXT_FIELDS.slice(0, 2).map(([k, type, ac]) => (
                  <div key={k}>
                    <Label htmlFor={k}>{r.labels[k]}</Label>
                    <Input id={k} type={type} autoComplete={ac} required value={form[k]} onChange={(e) => set(k, e.target.value)} className={fieldCls} data-testid={`lead-form-${k}-input`} />
                  </div>
                ))}
                <div>
                  <Label htmlFor="country">{r.labels.country}</Label>
                  <Select value={form.country} onValueChange={(v) => set("country", v)}>
                    <SelectTrigger id="country" className={fieldCls} data-testid="lead-form-country-select">
                      <SelectValue placeholder="Selecciona tu país" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {countries.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`country-option-${c}`}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {TEXT_FIELDS.slice(2).map(([k, type, ac]) => (
                  <div key={k}>
                    <Label htmlFor={k}>{r.labels[k]}</Label>
                    <Input id={k} type={type} autoComplete={ac} required value={form[k]} onChange={(e) => set(k, e.target.value)} className={fieldCls} placeholder={k === "whatsapp" ? "+52 55 0000 0000" : undefined} data-testid={`lead-form-${k}-input`} />
                  </div>
                ))}
                <div className="hidden sm:block" />
                {YES_NO.map((k) => (
                  <YesNo key={k} name={k} label={r.labels[k]} value={form[k]} onChange={set} />
                ))}
                <div className="sm:col-span-2">
                  <Label htmlFor="referral_code">{r.labels.referral_code}</Label>
                  <Input id="referral_code" value={form.referral_code} onChange={(e) => set("referral_code", e.target.value)} className={fieldCls} data-testid="lead-form-referral_code-input" />
                </div>
                <label htmlFor="accepted_terms" className="flex cursor-pointer items-start gap-3 rounded-2xl border border-ink/15 bg-paper/60 p-4 sm:col-span-2">
                  <Checkbox id="accepted_terms" checked={form.accepted_terms} onCheckedChange={(v) => set("accepted_terms", v === true)} className="mt-0.5 h-5 w-5 border-ink/40 data-[state=checked]:bg-ink" data-testid="lead-form-terms-checkbox" />
                  <span className="text-sm leading-relaxed text-ink/85">{r.disclaimer}</span>
                </label>
                <p className="-mt-2 text-xs leading-relaxed text-ink/70 sm:col-span-2" data-testid="lead-form-privacy-note">
                  Al enviar tus datos aceptas nuestro{" "}
                  <Link to="/legal/privacidad" target="_blank" className="font-semibold text-leaf-deep underline underline-offset-2" data-testid="lead-form-privacy-link">Aviso de Privacidad</Link>{" "}
                  y los{" "}
                  <Link to="/legal/terminos" target="_blank" className="font-semibold text-leaf-deep underline underline-offset-2" data-testid="lead-form-terms-link">Términos del servicio</Link>.
                </p>
                <button type="submit" disabled={loading} className="btn-leaf group w-full !py-5 disabled:opacity-60 sm:col-span-2" data-testid="lead-form-submit-btn">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{r.submit} <ArrowUpRight className="btn-arrow h-5 w-5" /></>}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
};
